// Read-only, deterministic production-source inventory.
// Mirrors Textbook's source-authority-first publication sequence, but creates
// no manuscript lock, snapshot, rendered PDF, release record or export.
const clean = (value) => String(value ?? "").trim();
const safeArray = (value) => Array.isArray(value) ? value : [];

// Match the backend Novel source snapshot's readable-writing contract.
// Legacy body/content/sceneText fields are not captured into production source.
export function hasReadableNovelSavedSourceText(item) {
  if (clean(item?.text)) return true;
  return safeArray(item?.manuscriptContent?.blocks).some((block) =>
    clean(block?.text) || safeArray(block?.runs).some((run) => clean(run?.text))
  );
}

function textFromContent(content) {
  if (!content || typeof content !== "object") return "";
  const blocks = safeArray(content.blocks);
  return blocks.map((block) => {
    const runs = safeArray(block?.runs).map((run) => String(run?.text ?? "")).join("");
    return clean(runs) ? runs : String(block?.text ?? "");
  }).filter((value) => clean(value)).join(" ");
}

function sourceText(item) {
  const saved = textFromContent(item?.manuscriptContent);
  // Only stored source fields are counted, never unsupported legacy fallbacks.
  return clean(saved) ? saved : String(item?.text ?? "");
}

function wordCount(text) {
  const value = clean(text);
  return value ? value.split(/\s+/).length : 0;
}

function sourceId(value) {
  return clean(value);
}

export function buildNovelProductionSourceManifest({
  title = "", frontMatter = [], chapters = [], backMatter = [],
  sceneById, unassignedSceneIds = [],
} = {}) {
  const sections = [];
  const issues = [];
  const seenSceneIds = new Set();
  const seenMatterIds = { front: new Set(), back: new Set() };
  const seenChapterIds = new Set();
  let sceneCount = 0;
  let textWords = 0;

  function issue(code, location, description) {
    issues.push({ code, location, description, severity: "blocking" });
  }

  ["front", "chapter", "back"].forEach((kind) => {
    const items = kind === "front" ? safeArray(frontMatter) :
      kind === "chapter" ? safeArray(chapters) : safeArray(backMatter);
    items.forEach((item, index) => {
      const id = sourceId(item?.id);
      const name = clean(item?.title);
      const displayTitle = name || (kind === "chapter" ? "Chapter " + (index + 1) :
        (kind === "front" ? "Front Matter " : "Back Matter ") + (index + 1));
      const location = displayTitle + " (" + (kind === "chapter" ? "Chapter" : kind === "front" ? "Front Matter" : "Back Matter") + ")";
      if (!id) issue("missing-section-id", location, "Saved section identity is missing");
      if (!name) issue("missing-section-title", location, "Saved section title is missing");
      const registry = kind === "chapter" ? seenChapterIds : seenMatterIds[kind];
      if (id && registry.has(id)) issue("duplicate-section-id", location, "Section identity appears more than once");
      if (id) registry.add(id);

      if (kind !== "chapter") {
        const words = wordCount(sourceText(item));
        textWords += words;
        if (!words) issue("empty-matter", location, "Book matter has no readable saved content");
        sections.push({ kind, id, title: displayTitle, index, sceneIds: [], sceneCount: 0, wordCount: words });
        return;
      }

      const sceneIds = [];
      let chapterWords = 0;
      const rawIds = safeArray(item?.sceneIds);
      if (!rawIds.length) issue("empty-chapter", location, "Chapter has no saved Canon Scene assignments");
      rawIds.forEach((rawId) => {
        const sceneId = sourceId(rawId);
        if (!sceneId) {
          issue("missing-scene-id", location, "Chapter contains an empty Scene identity");
          return;
        }
        if (seenSceneIds.has(sceneId)) {
          issue("duplicate-scene", location, "Scene " + sceneId + " is assigned more than once");
          return;
        }
        seenSceneIds.add(sceneId);
        const scene = sceneById?.get?.(sceneId) || sceneById?.get?.(rawId);
        if (!scene) {
          issue("missing-canon-scene", location, "Assigned Scene " + sceneId + " has no saved Canon source");
          return;
        }
        const words = wordCount(sourceText(scene));
        if (!words) issue("empty-canon-scene", location, "Assigned Scene " + sceneId + " has no saved text");
        sceneIds.push(sceneId);
        chapterWords += words;
        sceneCount += 1;
      });
      textWords += chapterWords;
      sections.push({ kind, id, title: displayTitle, index, sceneIds, sceneCount: sceneIds.length, wordCount: chapterWords });
    });
  });

  if (!safeArray(chapters).length) issue("no-chapters", "Main manuscript", "No saved Chapters");
  const unassigned = Array.from(new Set(safeArray(unassignedSceneIds).map(sourceId).filter(Boolean)));
  if (unassigned.length) issue("unassigned-canon-scenes", "Unassigned Canon Scenes",
    unassigned.length + " saved Canon Scenes are not part of the production assembly");

  return {
    title: clean(title),
    status: "source-preview-only",
    order: "front-matter > chapters > back-matter",
    sections, issues, sceneCount, wordCount: textWords,
    unassignedSceneCount: unassigned.length,
    readyForProductionContract: issues.length === 0,
    // A source inventory is never itself authority to lock, print, publish, or export.
    publicationSnapshotId: null,
    finalInteriorPdf: null,
    finalPageCount: null,
    coverGeometry: null,
  };
}
